// features/admin/warrantyApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface Warranty {
  id: number;
  product_id: number;
  prod_name?: string;
  warranty_name: string;
  warranty_period: string;
  no_of_period: number;
  description: string;
  features: string;
  price?: number;
  additional_cost?: number;
  created_at?: string;
  updated_at?: string;
}

export interface WarrantyFormData {
  product_id: number;
  warranty_name: string;
  warranty_period: string;
  no_of_period: number;
  description: string;
  features: string;
  price?: number;
  additional_cost?: number;
  id?: number;
}

export interface WarrantyResponse {
  success: boolean;
  message: string;
  data?: Warranty[];
  count?: number;
}

export interface WarrantyRequest {
  product_id: number;
  warranty_name: string;
  warranty_period: string;
  no_of_period: number;
  description: string;
  features: string;
  price?: number;
  additional_cost?: number;
}

export interface UpdateWarrantyRequest extends WarrantyRequest {
  id: number;
}

export interface DeleteWarrantyRequest {
  id: number;
}

export interface ListWarrantyByProductRequest {
  product_id: number;
}

// Period options
export const WARRANTY_PERIOD_OPTIONS = [
  { value: 'month', label: 'Month(s)' },
  { value: 'year', label: 'Year(s)' },
  { value: 'day', label: 'Day(s)' },
];

export const warrantyApi = createApi({
  reducerPath: 'warrantyApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return headers;
    },
  }),
  tagTypes: ['Warranty'],
  endpoints: (build) => ({
    // List all warranties
    listAllWarranties: build.query<WarrantyResponse, void>({
      query: () => ({
        url: 'user/list-allwarranty',
        method: 'POST',
        body: new URLSearchParams(),
      }),
      providesTags: ['Warranty'],
      transformResponse: (response: any) => ({
        success: response.success || false,
        message: response.message || '',
        data: response.data || [],
        count: response.count || response.data?.length || 0,
      }),
    }),

    // List warranties by product ID
    listWarrantiesByProduct: build.query<WarrantyResponse, ListWarrantyByProductRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('product_id', data.product_id.toString());

        return {
          url: 'user/listwarranty',
          method: 'POST',
          body: formData,
        };
      },
      providesTags: ['Warranty'],
      transformResponse: (response: any) => ({
        success: response.success || false,
        message: response.message || '',
        data: response.data || [],
        count: response.count || response.data?.length || 0,
      }),
    }),

    // Add warranty
    addWarranty: build.mutation<WarrantyResponse, WarrantyRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('product_id', data.product_id.toString());
        formData.append('warranty_name', data.warranty_name.trim());
        formData.append('warranty_period', data.warranty_period);
        formData.append('no_of_period', data.no_of_period.toString());
        formData.append('description', data.description.trim());
        formData.append('features', data.features.trim());
        
        if (data.price !== undefined) {
          formData.append('price', data.price.toString());
        }
        
        if (data.additional_cost !== undefined) {
          formData.append('additional_cost', data.additional_cost.toString());
        }

        return {
          url: 'user/addwarranty',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Warranty'],
    }),

    // Update warranty
    updateWarranty: build.mutation<WarrantyResponse, UpdateWarrantyRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('id', data.id.toString());
        formData.append('product_id', data.product_id.toString());
        formData.append('warranty_name', data.warranty_name.trim());
        formData.append('warranty_period', data.warranty_period);
        formData.append('no_of_period', data.no_of_period.toString());
        formData.append('description', data.description.trim());
        formData.append('features', data.features.trim());
        
        if (data.price !== undefined) {
          formData.append('price', data.price.toString());
        }
        
        if (data.additional_cost !== undefined) {
          formData.append('additional_cost', data.additional_cost.toString());
        }

        return {
          url: 'user/updatewarranty',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Warranty'],
    }),

    // Delete warranty
    deleteWarranty: build.mutation<WarrantyResponse, DeleteWarrantyRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('id', data.id.toString());

        return {
          url: 'user/deletewarranty',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Warranty'],
    }),
  }),
});

export const {
  useListAllWarrantiesQuery,
  useListWarrantiesByProductQuery,
  useAddWarrantyMutation,
  useUpdateWarrantyMutation,
  useDeleteWarrantyMutation,
} = warrantyApi;